import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import {
  BULK_CASE_ACTION_ENDPOINT,
  BULK_CASE_ACTION_COUNT_ENDPOINT,
  BULK_CASE_ACTION_RUN_ENDPOINT,
  BULK_CASE_ACTION_STATUS_ENDPOINT,
} from '@app/constants/endpoints';
import {
  BULK_CASE_ACTION_KIND,
  type BulkCaseActionKind,
} from '@app/constants/queue';
import { QUERY_KEYS } from '@app/constants/service';
import { fetchData, postData } from '@app/hooks/react-query-helpers';

export interface BulkCaseActionFilter {
  isOpen?: boolean;
  operatorIds?: string[];
  groupIds?: string[];
  workflowStatus?: string;
  queueFilter?: string;
  exposureTypeIds?: string[];
  caseTypes?: string[];
  caseIds?: string[];
}

export interface BulkCaseActionScopeCount {
  totalMatched: number;
  notVisible: number;
}

export type BulkCaseActionSubmitRequest = { filter: BulkCaseActionFilter } & (
  | {
      kind: typeof BULK_CASE_ACTION_KIND.QUEUE_SET;
      payload: { queueId: string };
    }
  | {
      kind: typeof BULK_CASE_ACTION_KIND.QUEUE_REMOVE;
      payload: Record<string, never>;
    }
  | {
      kind: typeof BULK_CASE_ACTION_KIND.OPERATOR_SET;
      payload: { operatorId: string };
    }
  | {
      kind: typeof BULK_CASE_ACTION_KIND.CASE_CLOSE;
      payload: Record<string, never>;
    }
);

export interface BulkCaseActionSubmitResult extends BulkCaseActionScopeCount {
  runId: string;
}

export interface BulkCaseActionOrgStatus {
  inProgress: boolean;
  enabled: boolean;
  assignAndCloseEnabled: boolean;
  kind: BulkCaseActionKind | null;
}

export interface BulkCaseActionRunComplete {
  runId: string;
  status: string;
  kind: BulkCaseActionKind;
  payload: Record<string, unknown>;
  snapshotSize: number;
  notVisible: number;
  succeededCount: number;
  skippedCount: number;
  failedCount: number;
  sideEffectFailures: number;
  startedAt?: string;
  finishedAt?: string;
}

export interface BulkCaseActionRunItem {
  caseId: string;
  status: string;
  reasonKey?: string;
}

export interface BulkCaseActionRunPagination {
  count: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  nextPage: number | null;
  prevPage: number | null;
}

export interface BulkCaseActionRunStatus extends BulkCaseActionRunComplete {
  items: BulkCaseActionRunItem[];
  pagination: BulkCaseActionRunPagination;
}

// A run lasts minutes and every queue admin polls this.
const BULK_CASE_ACTION_STATUS_POLL_INTERVAL_MS = 30000;

export const FIRST_RUN_ITEMS_PAGE = 1;

export const useBulkCaseActionCountQuery = (
  filter: BulkCaseActionFilter | null,
  enabled = true,
  kind?: BulkCaseActionKind,
) =>
  useQuery<BulkCaseActionScopeCount>({
    queryKey: [QUERY_KEYS.BULK_CASE_ACTION_COUNT, filter, kind],
    queryFn: async () =>
      postData(
        BULK_CASE_ACTION_COUNT_ENDPOINT,
        kind ? { filter, kind } : { filter },
        { handleError: true },
      ),
    enabled: enabled && Boolean(filter),
    // A stale count would let an admin commit to a scope the filter no longer matches.
    staleTime: 0,
  });

export const BULK_CASE_ACTION_CASES_CHANGED_KEY = [
  QUERY_KEYS.BULK_CASE_ACTION_CASES_CHANGED,
];

export const useBulkCaseActionSubmitMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<
    BulkCaseActionSubmitResult,
    Error,
    BulkCaseActionSubmitRequest
  >({
    mutationFn: async (request) =>
      postData(BULK_CASE_ACTION_ENDPOINT, request, { handleError: true }),
    onSuccess: ({ runId }, { kind }) => {
      const finishedRunId = queryClient.getQueryData<string | null>(
        BULK_CASE_ACTION_CASES_CHANGED_KEY,
      );
      if (finishedRunId === runId) return;
      queryClient.setQueryData<BulkCaseActionOrgStatus>(
        [QUERY_KEYS.BULK_CASE_ACTION_STATUS],
        (status) => (status ? { ...status, inProgress: true, kind } : status),
      );
    },
  });
};

export const useBulkCaseActionOrgStatusQuery = (enabled: boolean) =>
  useQuery<BulkCaseActionOrgStatus>({
    queryKey: [QUERY_KEYS.BULK_CASE_ACTION_STATUS],
    queryFn: async () => fetchData(BULK_CASE_ACTION_STATUS_ENDPOINT),
    enabled,
    refetchInterval: BULK_CASE_ACTION_STATUS_POLL_INTERVAL_MS,
    refetchOnMount: true,
  });

// Websocket handlers run outside React with a queryClient but no store to write to.
const CLIENT_STATE_OPTIONS = {
  enabled: false,
  staleTime: Infinity,
  cacheTime: Infinity,
};

export const BULK_CASE_ACTION_RESULT_RUN_KEY = [
  QUERY_KEYS.BULK_CASE_ACTION_RESULT_RUN,
];

export const setBulkCaseActionResultRun = (
  queryClient: QueryClient,
  runId: string | null,
): void => {
  queryClient.setQueryData(BULK_CASE_ACTION_RESULT_RUN_KEY, runId);
};

export const useBulkCaseActionResultRun = (): string | null =>
  useQuery<string | null>({
    queryKey: BULK_CASE_ACTION_RESULT_RUN_KEY,
    queryFn: () => null,
    initialData: null,
    ...CLIENT_STATE_OPTIONS,
  }).data ?? null;

// The run id, not a counter: one run arrives on two topics and must reload once.
export const signalBulkCaseActionCasesChanged = (
  queryClient: QueryClient,
  runId?: string | null,
): void => {
  queryClient.setQueryData<string | null>(
    BULK_CASE_ACTION_CASES_CHANGED_KEY,
    runId ?? null,
  );
};

export const useBulkCaseActionCasesChangedSignal = (): string | null =>
  useQuery<string | null>({
    queryKey: BULK_CASE_ACTION_CASES_CHANGED_KEY,
    queryFn: () => null,
    initialData: null,
    ...CLIENT_STATE_OPTIONS,
  }).data;

export const BULK_CASE_ACTION_CASES_RELOADED_KEY = [
  QUERY_KEYS.BULK_CASE_ACTION_CASES_RELOADED,
];

// The signal outlives the Cases page, so a remount must not replay a finished run.
export const markBulkCaseActionCasesReloaded = (
  queryClient: QueryClient,
  runId: string,
): void => {
  queryClient.setQueryData<string | null>(
    BULK_CASE_ACTION_CASES_RELOADED_KEY,
    runId,
  );
};

export const useBulkCaseActionCasesReloadedRunId = (): string | null =>
  useQuery<string | null>({
    queryKey: BULK_CASE_ACTION_CASES_RELOADED_KEY,
    queryFn: () => null,
    initialData: null,
    ...CLIENT_STATE_OPTIONS,
  }).data;

export const fetchAllBulkCaseActionRunItems = async (
  runId: string,
): Promise<BulkCaseActionRunItem[]> => {
  const items: BulkCaseActionRunItem[] = [];
  let page: number | null = FIRST_RUN_ITEMS_PAGE;
  while (page !== null) {
    // eslint-disable-next-line no-await-in-loop -- each page's cursor comes from the last response
    const response: BulkCaseActionRunStatus = await fetchData(
      BULK_CASE_ACTION_RUN_ENDPOINT(runId, page),
    );
    items.push(...(response?.items ?? []));
    const nextPage = response?.pagination?.nextPage ?? null;
    // A nextPage that never advances would loop forever.
    page = nextPage !== null && nextPage > page ? nextPage : null;
  }
  return items;
};

export const useBulkCaseActionRunStatusQuery = (
  runId: string | null,
  page: number,
) =>
  useQuery<BulkCaseActionRunStatus>({
    queryKey: [QUERY_KEYS.BULK_CASE_ACTION_RUN, runId, page],
    queryFn: async () =>
      fetchData(BULK_CASE_ACTION_RUN_ENDPOINT(runId as string, page)),
    enabled: Boolean(runId),
  });
